import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { TimezoneSelect } from 'components/ui/timezone-select';
import {
  submitIntake,
  fetchIntakeProfile,
  IntakeResponse,
  IntakeProfile,
} from '../../api/intake';
import { IntakeProfileReveal } from './intake-profile-reveal';
import { AppointmentModal } from '../appointment-modal';
import { Therapist } from '../../types/user';
import { authenticatedFetch } from '../../utils/api';

// ──────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────

type Step =
  | 'loading'
  | 'intro'
  | 'q_origin'
  | 'q_location'
  | 'q_situation'
  | 'q_time_abroad'
  | 'q_stressors'
  | 'q_mood'
  | 'q_language'
  | 'q_therapist_pref'
  | 'submitting'
  | 'processing'
  | 'reveal'
  | 'complete';

const QUESTION_STEPS: Step[] = [
  'q_origin',
  'q_location',
  'q_situation',
  'q_time_abroad',
  'q_stressors',
  'q_mood',
  'q_language',
  'q_therapist_pref',
];

// ──────────────────────────────────────────────
// Data definitions
// ──────────────────────────────────────────────

interface CountryOption {
  code: string;
  name: string;
  flag: string;
}

const ORIGIN_COUNTRIES: CountryOption[] = [
  { code: 'CI', name: "Côte d'Ivoire", flag: '🇨🇮' },
  { code: 'SN', name: 'Sénégal', flag: '🇸🇳' },
  { code: 'BJ', name: 'Bénin', flag: '🇧🇯' },
  { code: 'ML', name: 'Mali', flag: '🇲🇱' },
  { code: 'BF', name: 'Burkina Faso', flag: '🇧🇫' },
  { code: 'GN', name: 'Guinée', flag: '🇬🇳' },
  { code: 'TG', name: 'Togo', flag: '🇹🇬' },
  { code: 'NE', name: 'Niger', flag: '🇳🇪' },
  { code: 'CM', name: 'Cameroun', flag: '🇨🇲' },
  { code: 'CD', name: 'RD Congo', flag: '🇨🇩' },
  { code: 'GH', name: 'Ghana', flag: '🇬🇭' },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
];

const HOST_COUNTRIES: CountryOption[] = [
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'BE', name: 'Belgique', flag: '🇧🇪' },
  { code: 'CH', name: 'Suisse', flag: '🇨🇭' },
  { code: 'US', name: 'États-Unis', flag: '🇺🇸' },
  { code: 'GB', name: 'Royaume-Uni', flag: '🇬🇧' },
  { code: 'DE', name: 'Allemagne', flag: '🇩🇪' },
  { code: 'MA', name: 'Maroc', flag: '🇲🇦' },
  { code: 'TN', name: 'Tunisie', flag: '🇹🇳' },
];

interface StressorOption {
  id: string;
  label: string;
  icon: string;
}

const STRESSORS: StressorOption[] = [
  { id: 'stress_academique', label: 'Pression académique', icon: '📚' },
  { id: 'isolement', label: 'Solitude', icon: '🏠' },
  { id: 'famille', label: 'Famille', icon: '👨‍👩‍👧' },
  { id: 'identite', label: 'Identité', icon: '🪞' },
  { id: 'discrimination', label: 'Discrimination', icon: '🚫' },
  { id: 'anxiete', label: 'Anxiété', icon: '😰' },
  { id: 'sommeil', label: 'Sommeil', icon: '😴' },
  { id: 'relations', label: 'Relations', icon: '💔' },
  { id: 'adaptation', label: 'Adaptation', icon: '🌍' },
];

const LANGUAGE_OPTIONS = [
  { id: 'fr', label: 'Français' },
  { id: 'en', label: 'English' },
  { id: 'dioula', label: 'Dioula' },
  { id: 'wolof', label: 'Wolof' },
  { id: 'bambara', label: 'Bambara' },
  { id: 'fon', label: 'Fon' },
  { id: 'lingala', label: 'Lingala' },
  { id: 'ewé', label: 'Ewé' },
  { id: 'haoussa', label: 'Haoussa' },
  { id: 'yoruba', label: 'Yoruba' },
  { id: 'igbo', label: 'Igbo' },
];

const TIME_ABROAD_OPTIONS = [
  { id: '<6m', label: '< 6 mois' },
  { id: '6m-1y', label: '6 mois – 1 an' },
  { id: '1-2y', label: '1 – 2 ans' },
  { id: '2-5y', label: '2 – 5 ans' },
  { id: '5y+', label: '5+ ans' },
];

const MOOD_EMOJIS = [
  { value: 1, emoji: '😣', label: 'Très mal' },
  { value: 2, emoji: '😔', label: 'Pas bien' },
  { value: 3, emoji: '😐', label: 'Neutre' },
  { value: 4, emoji: '🙂', label: 'Bien' },
  { value: 5, emoji: '😊', label: 'Très bien' },
];

// ──────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────

export const IntakePage: React.FC = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('loading');
  const [responses, setResponses] = useState<Record<string, IntakeResponse>>({});
  const [profile, setProfile] = useState<IntakeProfile | null>(null);
  const [error, setError] = useState('');
  const [isReferred, setIsReferred] = useState(false);
  const [processingPhase, setProcessingPhase] = useState(0);
  const [selectedTherapist, setSelectedTherapist] = useState<Therapist | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [profileForm, setProfileForm] = useState({ firstName: '', lastName: '', timezone: 'Africa/Abidjan', phoneNumber: '', countryCode: '+225' });
  const [profileSaving, setProfileSaving] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Ensure French locale on mount (intake is always in French)
  useEffect(() => {
    if (i18n.language !== 'fr') {
      i18n.changeLanguage('fr');
    }
  }, []);

  // Check for existing intake on mount
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.referredByTherapistId) {
          setIsReferred(true);
        }
      } catch {}
    }

    fetchIntakeProfile()
      .then((p) => {
        if (p.status === 'completed') {
          // Intake already done — redirect to home (profile is viewable at /my-profile)
          window.location.href = '/home';
          return;
        } else if (p.status === 'processing') {
          setStep('processing');
          startPolling();
        } else {
          setStep('intro');
        }
      })
      .catch(() => {
        // 404 — no intake yet
        setStep('intro');
      });

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Advance processing phase for animated progress
  useEffect(() => {
    if (step !== 'processing') {
      setProcessingPhase(0);
      return;
    }
    const timer = setInterval(() => {
      setProcessingPhase((prev) => (prev < 3 ? prev + 1 : prev));
    }, 3500);
    return () => clearInterval(timer);
  }, [step]);

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    let attempts = 0;
    const maxAttempts = 45; // 45 × 2s = 90s timeout
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const p = await fetchIntakeProfile();
        if (p.status === 'completed') {
          if (pollRef.current) clearInterval(pollRef.current);
          setProfile(p);
          setStep('reveal');
        } else if (p.status === 'failed') {
          if (pollRef.current) clearInterval(pollRef.current);
          setError(t('intake.error'));
          setStep('intro');
        } else if (attempts >= maxAttempts) {
          if (pollRef.current) clearInterval(pollRef.current);
          setError(t('intake.error'));
          setStep('intro');
        }
      } catch {
        if (attempts >= maxAttempts) {
          if (pollRef.current) clearInterval(pollRef.current);
          setError(t('intake.error'));
          setStep('intro');
        }
      }
    }, 2000);
  }, [t]);

  // ── Helpers ──

  const updateResponse = (questionKey: string, partial: Partial<IntakeResponse>) => {
    setResponses((prev) => ({
      ...prev,
      [questionKey]: { ...prev[questionKey], questionKey, ...partial },
    }));
  };

  const currentQuestionIndex = QUESTION_STEPS.indexOf(step as any);
  const progress =
    currentQuestionIndex >= 0
      ? ((currentQuestionIndex + 1) / QUESTION_STEPS.length) * 100
      : 0;

  const canAdvance = (): boolean => {
    // Map step names to response keys (some differ)
    const stepToKey: Record<string, string> = { language: 'language_pref' };
    const rawKey = step.replace('q_', '');
    const key = stepToKey[rawKey] || rawKey;
    const r = responses[key];
    if (!r) return false;
    if (step === 'q_stressors' || step === 'q_language') {
      return !!(r.values && r.values.length > 0);
    }
    if (step === 'q_mood') {
      return r.numericVal !== undefined;
    }
    return !!r.value;
  };

  const goNext = () => {
    const idx = QUESTION_STEPS.indexOf(step as any);
    if (idx < QUESTION_STEPS.length - 1) {
      setStep(QUESTION_STEPS[idx + 1]);
    } else {
      handleSubmit();
    }
  };

  const goBack = () => {
    const idx = QUESTION_STEPS.indexOf(step as any);
    if (idx > 0) {
      setStep(QUESTION_STEPS[idx - 1]);
    } else {
      setStep('intro');
    }
  };

  const handleSubmit = async () => {
    setStep('submitting');
    setError('');
    try {
      const intakeResponses: IntakeResponse[] = Object.values(responses);
      await submitIntake(intakeResponses);
      setStep('processing');
      startPolling();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setStep('q_therapist_pref'); // Go back to last question
    }
  };

  const handleSkipIntake = () => {
    // Referred patient skipping intake — go directly to profile completion
    window.location.href = '/home';
  };

  const isProfileComplete = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      const user = JSON.parse(userStr);
      return !!(user.firstName && user.firstName.trim());
    } catch { return false; }
  };

  const handleCompleteProfile = () => {
    if (isProfileComplete()) {
      window.location.href = '/home';
    } else {
      setStep('complete');
    }
  };

  const handleBookFromReveal = (therapist: any) => {
    // Build a Therapist object from the backend data
    const th: Therapist = {
      id: therapist?.userId || therapist?.id || '',
      userId: therapist?.userId || therapist?.id || '',
      email: '',
      firstName: therapist?.user?.firstName || therapist?.firstName || '',
      lastName: therapist?.user?.lastName || therapist?.lastName || '',
      type: 'therapist',
      imageUrl: therapist?.imageUrl || '',
      name: therapist?.name || `Dr. ${therapist?.user?.firstName || ''}`,
      bio: therapist?.bio || '',
      specialties: therapist?.specialties || [],
      rating: therapist?.rating || 0,
      yearsExperience: therapist?.yearsExperience || 0,
      nextAvailable: therapist?.nextAvailable || '',
      languages: therapist?.languages || [],
    };
    setSelectedTherapist(th);

    if (isProfileComplete()) {
      setShowBookingModal(true);
    } else {
      setStep('complete');
    }
  };

  const handleProfileFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileSaving(true);
    setError('');
    try {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : {};
      const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL}/api/auth/complete-profile`, {
        method: 'POST',
        body: JSON.stringify({
          email: user.email,
          firstName: profileForm.firstName,
          lastName: profileForm.lastName,
          timezone: profileForm.timezone,
          phoneNumber: profileForm.phoneNumber ? `${profileForm.countryCode}${profileForm.phoneNumber}` : undefined,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('intake.error'));

      localStorage.setItem('token', data.token);
      data.user.type = 'patient';
      localStorage.setItem('user', JSON.stringify(data.user));

      // Profile complete — open booking modal if therapist was selected
      if (selectedTherapist) {
        setShowBookingModal(true);
      } else {
        window.location.href = '/home';
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setProfileSaving(false);
    }
  };

  // ── Encourage text between questions ──
  const encouragements = [
    t('intake.encourage1'),
    t('intake.encourage2'),
    t('intake.encourage3'),
    t('intake.encourage4'),
  ];

  // ── Render ──

  if (step === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-cyan-50">
        <div className="w-10 h-10 rounded-full border-4 border-[#00c4cc] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (step === 'reveal' && profile) {
    return (
      <>
        <IntakeProfileReveal
          profile={profile}
          onBook={handleBookFromReveal}
          onComplete={handleCompleteProfile}
        />
        {selectedTherapist && (
          <AppointmentModal
            isOpen={showBookingModal}
            onClose={() => setShowBookingModal(false)}
            therapist={selectedTherapist}
          />
        )}
      </>
    );
  }

  if (step === 'complete') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-cyan-50 flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full space-y-6 animate-fadeIn">
          <div className="text-center space-y-2">
            <div className="w-16 h-16 mx-auto bg-gradient-to-br from-[#00c4cc] to-[#00a3ab] rounded-full flex items-center justify-center mb-4">
              <span className="text-2xl">👤</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-800">
              {t('intake.completeProfileTitle')}
            </h2>
            <p className="text-gray-500">
              {t('intake.completeProfileSubtitle')}
            </p>
          </div>

          <form onSubmit={handleProfileFormSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">{t('intake.firstName')}</label>
              <Input
                type="text"
                placeholder={t('intake.firstNamePlaceholder')}
                value={profileForm.firstName}
                onChange={(e) => setProfileForm(prev => ({ ...prev, firstName: e.target.value }))}
                autoFocus
                data-testid="profile-firstname"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">{t('intake.lastName')}</label>
              <Input
                type="text"
                placeholder={t('intake.lastNamePlaceholder')}
                value={profileForm.lastName}
                onChange={(e) => setProfileForm(prev => ({ ...prev, lastName: e.target.value }))}
                data-testid="profile-lastname"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <svg className="w-4 h-4 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </label>
              <div className="flex gap-2">
                <select
                  className="h-10 rounded-md border border-input bg-background px-2 text-sm"
                  value={profileForm.countryCode}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, countryCode: e.target.value }))}
                >
                  <option value="+225">🇨🇮 +225</option>
                  <option value="+221">🇸🇳 +221</option>
                  <option value="+229">🇧🇯 +229</option>
                  <option value="+243">🇨🇩 +243</option>
                  <option value="+237">🇨🇲 +237</option>
                </select>
                <Input
                  type="tel"
                  placeholder="XX XX XX XX XX"
                  value={profileForm.phoneNumber}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setProfileForm(prev => ({ ...prev, phoneNumber: digits }));
                  }}
                  className="flex-1"
                  data-testid="profile-phone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">{t('intake.timezone')}</label>
              <TimezoneSelect
                value={profileForm.timezone}
                onValueChange={(value) => setProfileForm(prev => ({ ...prev, timezone: value }))}
              />
            </div>

            {error && <p className="text-red-500 text-sm text-center">{error}</p>}

            <Button
              type="submit"
              className="w-full text-white text-lg py-6"
              style={{ backgroundColor: '#00c4cc' }}
              disabled={profileSaving || !profileForm.firstName.trim() || !profileForm.lastName.trim() || profileForm.phoneNumber.length < 8}
              data-testid="profile-submit-btn"
            >
              {profileSaving ? t('intake.saving') : (selectedTherapist ? t('intake.completeAndBook') : t('intake.completeAndContinue'))}
            </Button>
          </form>
        </div>

        {selectedTherapist && (
          <AppointmentModal
            isOpen={showBookingModal}
            onClose={() => {
              setShowBookingModal(false);
              window.location.href = '/home';
            }}
            therapist={selectedTherapist}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-cyan-50 flex flex-col">
      {/* Progress bar */}
      {currentQuestionIndex >= 0 && (
        <div className="fixed top-0 left-0 right-0 h-1.5 bg-gray-200 z-50" data-testid="intake-progress">
          <div
            className="h-full bg-[#00c4cc] transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
            data-testid="intake-progress-bar"
          />
        </div>
      )}

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8 max-w-lg mx-auto w-full">
        {/* Back button */}
        {currentQuestionIndex >= 0 && (
          <button
            onClick={goBack}
            className="self-start mb-4 text-gray-500 hover:text-gray-700 flex items-center gap-1"
            data-testid="intake-back-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('intake.back')}
          </button>
        )}

        {/* ── Intro ── */}
        {step === 'intro' && (
          <div className="text-center space-y-6 animate-fadeIn" data-testid="intake-intro">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#00c4cc] to-[#00a3ab] rounded-full flex items-center justify-center">
              <span className="text-3xl">🌍</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-800">
              {t('intake.introTitle')}
            </h1>
            <p className="text-gray-600 text-lg">
              {t('intake.introSubtitle')}
            </p>
            <div className="bg-white rounded-xl p-5 shadow-sm space-y-3 text-left">
              <div className="flex items-center gap-3">
                <span className="text-xl">⏱️</span>
                <span className="text-gray-700">{t('intake.introStep1')}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xl">👆</span>
                <span className="text-gray-700">{t('intake.introStep2')}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xl">🤝</span>
                <span className="text-gray-700">{t('intake.introStep3')}</span>
              </div>
            </div>
            <Button
              onClick={() => setStep('q_origin')}
              className="w-full text-white text-lg py-6"
              style={{ backgroundColor: '#00c4cc' }}
              data-testid="intake-start-btn"
            >
              {t('intake.start')}
            </Button>
            {isReferred && (
              <button
                onClick={handleSkipIntake}
                className="text-gray-500 hover:text-gray-700 underline text-sm"
                data-testid="intake-skip-btn"
              >
                {t('intake.skip')}
              </button>
            )}
          </div>
        )}

        {/* ── Origin country ── */}
        {step === 'q_origin' && (
          <QuestionWrapper
            title={t('intake.originTitle')}
            subtitle={t('intake.originSubtitle')}
            testId="intake-q-origin"
          >
            <div className="grid grid-cols-3 gap-3">
              {ORIGIN_COUNTRIES.map((c) => (
                <button
                  key={c.code}
                  onClick={() => updateResponse('origin', { value: c.code, method: 'tap' })}
                  className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                    responses.origin?.value === c.code
                      ? 'border-[#00c4cc] bg-cyan-50 scale-105'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`country-${c.code}`}
                >
                  <span className="text-3xl">{c.flag}</span>
                  <span className="text-xs text-gray-700 text-center">{c.name}</span>
                </button>
              ))}
            </div>
          </QuestionWrapper>
        )}

        {/* ── Location (host country) ── */}
        {step === 'q_location' && (
          <QuestionWrapper
            title={t('intake.locationTitle')}
            subtitle={t('intake.locationSubtitle')}
            testId="intake-q-location"
          >
            <div className="grid grid-cols-3 gap-3">
              {HOST_COUNTRIES.map((c) => (
                <button
                  key={c.code}
                  onClick={() => updateResponse('location', { value: c.code, method: 'tap' })}
                  className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                    responses.location?.value === c.code
                      ? 'border-[#00c4cc] bg-cyan-50 scale-105'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`country-${c.code}`}
                >
                  <span className="text-3xl">{c.flag}</span>
                  <span className="text-xs text-gray-700 text-center">{c.name}</span>
                </button>
              ))}
            </div>
          </QuestionWrapper>
        )}

        {/* ── Situation ── */}
        {step === 'q_situation' && (
          <QuestionWrapper
            title={t('intake.situationTitle')}
            testId="intake-q-situation"
          >
            <div className="space-y-3">
              {[
                { id: 'student', icon: '🎓', label: t('intake.situationStudent') },
                { id: 'working', icon: '💼', label: t('intake.situationWorking') },
                { id: 'seeking', icon: '🔍', label: t('intake.situationSeeking') },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => updateResponse('situation', { value: opt.id, method: 'tap' })}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                    responses.situation?.value === opt.id
                      ? 'border-[#00c4cc] bg-cyan-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`situation-${opt.id}`}
                >
                  <span className="text-2xl">{opt.icon}</span>
                  <span className="text-gray-800 font-medium">{opt.label}</span>
                </button>
              ))}
            </div>
          </QuestionWrapper>
        )}

        {/* ── Time abroad ── */}
        {step === 'q_time_abroad' && (
          <QuestionWrapper
            title={t('intake.timeAbroadTitle')}
            testId="intake-q-time_abroad"
          >
            <div className="flex flex-wrap gap-3 justify-center">
              {TIME_ABROAD_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => updateResponse('time_abroad', { value: opt.id, method: 'tap' })}
                  className={`px-5 py-3 rounded-full border-2 transition-all font-medium ${
                    responses.time_abroad?.value === opt.id
                      ? 'border-[#00c4cc] bg-cyan-50 text-[#00c4cc]'
                      : 'border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                  data-testid={`time-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </QuestionWrapper>
        )}

        {/* ── Stressors (multi-select) ── */}
        {step === 'q_stressors' && (
          <QuestionWrapper
            title={t('intake.stressorsTitle')}
            subtitle={t('intake.stressorsSubtitle')}
            testId="intake-q-stressors"
          >
            <div className="flex flex-wrap gap-3 justify-center">
              {STRESSORS.map((s) => {
                const selected = responses.stressors?.values?.includes(s.id);
                return (
                  <button
                    key={s.id}
                    onClick={() => {
                      const current = responses.stressors?.values || [];
                      const next = selected
                        ? current.filter((v) => v !== s.id)
                        : [...current, s.id];
                      updateResponse('stressors', { values: next, method: 'tap' });
                    }}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full border-2 transition-all ${
                      selected
                        ? 'border-[#00c4cc] bg-cyan-50 text-[#00c4cc]'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                    data-testid={`chip-${s.id}`}
                  >
                    <span>{s.icon}</span>
                    <span className="font-medium text-sm">{s.label}</span>
                  </button>
                );
              })}
            </div>
          </QuestionWrapper>
        )}

        {/* ── Mood ── */}
        {step === 'q_mood' && (
          <QuestionWrapper
            title={t('intake.moodTitle')}
            testId="intake-q-mood"
          >
            <div className="flex justify-center gap-4">
              {MOOD_EMOJIS.map((m) => (
                <button
                  key={m.value}
                  onClick={() => updateResponse('mood', { numericVal: m.value, method: 'tap' })}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all ${
                    responses.mood?.numericVal === m.value
                      ? 'bg-cyan-50 scale-110'
                      : 'hover:bg-gray-50'
                  }`}
                  data-testid={`mood-${m.value}`}
                >
                  <span className="text-4xl">{m.emoji}</span>
                  <span className="text-xs text-gray-500">{m.label}</span>
                </button>
              ))}
            </div>
          </QuestionWrapper>
        )}

        {/* ── Language preference (multi-select) ── */}
        {step === 'q_language' && (
          <QuestionWrapper
            title={t('intake.languageTitle')}
            subtitle={t('intake.languageSubtitle')}
            testId="intake-q-language_pref"
          >
            <div className="flex flex-wrap gap-3 justify-center">
              {LANGUAGE_OPTIONS.map((l) => {
                const selected = responses.language_pref?.values?.includes(l.id);
                return (
                  <button
                    key={l.id}
                    onClick={() => {
                      const current = responses.language_pref?.values || [];
                      const next = selected
                        ? current.filter((v) => v !== l.id)
                        : [...current, l.id];
                      updateResponse('language_pref', { values: next, method: 'tap' });
                    }}
                    className={`px-4 py-2.5 rounded-full border-2 transition-all font-medium ${
                      selected
                        ? 'border-[#00c4cc] bg-cyan-50 text-[#00c4cc]'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                    data-testid={`lang-${l.id}`}
                  >
                    {l.label}
                  </button>
                );
              })}
            </div>
          </QuestionWrapper>
        )}

        {/* ── Therapist preference ── */}
        {step === 'q_therapist_pref' && (
          <QuestionWrapper
            title={t('intake.therapistPrefTitle')}
            testId="intake-q-therapist_pref"
          >
            <div className="space-y-3">
              {[
                { id: 'female', icon: '👩‍⚕️', label: t('intake.prefFemale') },
                { id: 'male', icon: '👨‍⚕️', label: t('intake.prefMale') },
                { id: 'none', icon: '🤝', label: t('intake.prefNone') },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => updateResponse('therapist_pref', { value: opt.id, method: 'tap' })}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                    responses.therapist_pref?.value === opt.id
                      ? 'border-[#00c4cc] bg-cyan-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`pref-${opt.id}`}
                >
                  <span className="text-2xl">{opt.icon}</span>
                  <span className="text-gray-800 font-medium">{opt.label}</span>
                </button>
              ))}
            </div>
          </QuestionWrapper>
        )}

        {/* ── Submitting ── */}
        {step === 'submitting' && (
          <div className="text-center space-y-4 animate-fadeIn">
            <div className="w-12 h-12 mx-auto rounded-full border-4 border-[#00c4cc] border-t-transparent animate-spin" />
            <p className="text-gray-600">{t('intake.submitting')}</p>
          </div>
        )}

        {/* ── Processing ── */}
        {step === 'processing' && (
          <div className="text-center space-y-6 animate-fadeIn" data-testid="intake-processing">
            {/* Animated icon per phase */}
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#00c4cc] to-[#00a3ab] rounded-full flex items-center justify-center shadow-lg">
              <span className="text-3xl transition-all duration-500" key={processingPhase}>
                {['🧠', '🌍', '🔍', '✨'][processingPhase]}
              </span>
            </div>

            {/* Phase-specific message */}
            <div className="min-h-[4rem]">
              <h2 className="text-xl font-bold text-gray-800 transition-opacity duration-300">
                {t(`intake.processingPhase${processingPhase}Title`)}
              </h2>
              <p className="text-gray-500 text-sm mt-2 transition-opacity duration-300">
                {t(`intake.processingPhase${processingPhase}Sub`)}
              </p>
            </div>

            {/* Smooth progress bar */}
            <div className="w-64 h-2 mx-auto bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#00c4cc] to-[#00a3ab] rounded-full"
                style={{
                  width: `${Math.min((processingPhase + 1) * 25, 95)}%`,
                  transition: 'width 1.5s ease',
                }}
              />
            </div>

            {/* Decorative floating dots */}
            <div className="flex justify-center gap-2 pt-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-full bg-[#00c4cc]/40"
                  style={{
                    animation: `pulse 1.5s ease-in-out ${i * 0.3}s infinite`,
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* ── Next button (for question steps) ── */}
        {currentQuestionIndex >= 0 && (
          <div className="w-full mt-8">
            {error && <p className="text-red-500 text-sm text-center mb-3">{error}</p>}
            <Button
              onClick={goNext}
              disabled={!canAdvance()}
              className="w-full text-white text-lg py-6"
              style={{ backgroundColor: canAdvance() ? '#00c4cc' : '#d1d5db' }}
              data-testid="intake-next-btn"
            >
              {currentQuestionIndex === QUESTION_STEPS.length - 1
                ? t('intake.finish')
                : t('intake.next')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────
// Question wrapper
// ──────────────────────────────────────────────

const QuestionWrapper: React.FC<{
  title: string;
  subtitle?: string;
  testId?: string;
  children: React.ReactNode;
}> = ({ title, subtitle, testId, children }) => (
  <div className="w-full space-y-6 animate-fadeIn" data-testid={testId}>
    <div className="text-center space-y-2">
      <h2 className="text-xl font-bold text-gray-800">{title}</h2>
      {subtitle && <p className="text-gray-500 text-sm">{subtitle}</p>}
    </div>
    {children}
  </div>
);

export default IntakePage;
